import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ShieldWarning, MagnifyingGlass, ArrowsClockwise, Wallet, UserCircle,
  ChatCircleDots, LockKey, TrendDown, ArrowRight, Copy,
} from "@phosphor-icons/react";

/**
 * AdminWalletDeductionsPage
 *
 * Super-admin only page that answers the question:
 *   "Where did this customer's wallet balance go?"
 *
 * Backed by GET /api/admin/wallet-deductions?q=... — the same lookup
 * matcher as /admin/customer-lookup, but unifying every debit source
 * (per-minute chat billing, tool unlocks, …) into one chronological
 * ledger. Use it to shut down false "my balance disappeared" claims.
 */

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtRelative(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffMin = (now - then) / 60000;
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${Math.round(diffMin)} min ago`;
    if (diffMin < 60 * 24) return `${(diffMin / 60).toFixed(1)} hr ago`;
    if (diffMin < 60 * 24 * 30) return `${(diffMin / (60 * 24)).toFixed(1)} d ago`;
    return `${(diffMin / (60 * 24 * 30)).toFixed(1)} mo ago`;
  } catch { return ""; }
}

const SOURCE_META = {
  chat: {
    label: "CHAT",
    tone: "bg-sky-100 text-sky-800 border-sky-300",
    Icon: ChatCircleDots,
  },
  tool_unlock: {
    label: "TOOL UNLOCK",
    tone: "bg-violet-100 text-violet-800 border-violet-300",
    Icon: LockKey,
  },
};

function SourcePill({ source }) {
  const meta = SOURCE_META[source] || {
    label: (source || "OTHER").toUpperCase(),
    tone: "bg-stone-100 text-stone-700 border-stone-300",
    Icon: TrendDown,
  };
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold tracking-wider ${meta.tone}`}
      data-testid={`source-pill-${source}`}
    >
      <Icon size={11} weight="fill" /> {meta.label}
    </span>
  );
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard?.writeText(String(value));
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="ml-1 inline-flex items-center opacity-60 hover:opacity-100 transition-opacity"
      title="Copy"
      data-testid="copy-btn"
    >
      <Copy size={11} weight="regular" /> {copied && <span className="ml-0.5 text-[9px] text-emerald-700">✓</span>}
    </button>
  );
}

export default function AdminWalletDeductionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null); // { query, found, results }

  const isSuperAdmin = !!user?.is_super_admin;

  const runSearch = async (e) => {
    e?.preventDefault?.();
    const query = q.trim();
    if (!query) { toast.error("Enter an email, name or user_id."); return; }
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/admin/wallet-deductions", {
        params: { q: query, limit },
      });
      setPayload(data);
      if (!data?.found) toast.info("No customer matched that query.");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Lookup failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Prime an empty state so the page doesn't look broken
    if (!isSuperAdmin) return;
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return (
      <PageShell>
        <Header />
        <div className="max-w-3xl mx-auto px-6 pt-20 pb-24 text-center" data-testid="admin-wallet-deductions-denied">
          <ShieldWarning size={48} weight="duotone" className="text-rose-400 mx-auto mb-4" />
          <h1 className="text-2xl font-serif-display text-stone-800 mb-2">Super-admin only</h1>
          <p className="text-stone-600">
            This page lets AstroKuber super-admins audit exactly where a customer&apos;s wallet balance went — chat billing, tool unlocks, and more.
          </p>
          <button
            onClick={() => navigate("/")}
            className="mt-6 px-5 py-2 rounded-full bg-stone-800 text-white hover:bg-stone-900"
            data-testid="back-home-btn"
          >
            Back to home
          </button>
        </div>
        <Footer />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Header />
      <main className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="admin-wallet-deductions-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-stone-500 mb-1">Super Admin</div>
            <h1 className="text-3xl sm:text-4xl font-serif-display text-stone-800">
              Wallet deductions audit
            </h1>
            <p className="text-stone-600 mt-1 text-sm max-w-2xl">
              Every rupee that left a customer&apos;s wallet — per-minute chat charges and tool unlocks — unified in one chronological ledger. Use it to answer the &ldquo;where did my balance go?&rdquo; question with proof.
            </p>
          </div>
          <Link
            to="/admin/customer-lookup"
            className="text-sm text-stone-600 hover:text-stone-900 inline-flex items-center gap-1"
            data-testid="link-to-customer-lookup"
          >
            <Wallet size={14} weight="duotone" /> Payment lookup <ArrowRight size={12} />
          </Link>
        </div>

        <form
          onSubmit={runSearch}
          className="mt-6 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end"
          data-testid="wallet-deductions-form"
        >
          <div className="flex-1">
            <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">Search by email, name or user_id</label>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="e.g. priya@example.com  or  Priya Sharma  or  user_abc123"
              className="w-full rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-stone-900 placeholder-stone-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
              data-testid="wallet-deductions-input"
            />
          </div>
          <div className="w-full sm:w-36">
            <label className="block text-[11px] uppercase tracking-widest text-stone-500 mb-1">Last N</label>
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value, 10))}
              className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-stone-900 focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
              data-testid="wallet-deductions-limit"
            >
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="rounded-lg bg-stone-800 hover:bg-stone-900 disabled:opacity-60 text-white px-5 py-2.5 font-medium inline-flex items-center justify-center gap-2"
            data-testid="wallet-deductions-submit"
          >
            {loading ? <ArrowsClockwise size={16} className="animate-spin" /> : <MagnifyingGlass size={16} weight="bold" />}
            {loading ? "Looking up…" : "Look up deductions"}
          </button>
        </form>

        {error && (
          <div className="mt-6 rounded-xl p-4 bg-rose-50 border border-rose-200 text-rose-900 text-sm" data-testid="wallet-deductions-error">
            {error}
          </div>
        )}

        {!loading && !error && payload && payload.found === 0 && (
          <div className="mt-10 rounded-2xl p-10 bg-amber-50 border border-amber-200 text-center" data-testid="wallet-deductions-empty">
            <UserCircle size={40} weight="duotone" className="text-stone-500 mx-auto mb-3" />
            <div className="text-stone-800 font-medium">No customer matched &ldquo;{payload.query}&rdquo;</div>
            <div className="text-stone-500 text-sm mt-1">Try a different fragment of their email, name, or the exact user_id.</div>
          </div>
        )}

        {payload && payload.found > 0 && (
          <div className="mt-8 space-y-8">
            {payload.results.map((r) => (
              <CustomerCard key={r.user_id} r={r} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </PageShell>
  );
}

function CustomerCard({ r }) {
  const s = r.summary || {};
  return (
    <div
      className="rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden"
      data-testid={`customer-card-${r.user_id}`}
    >
      {/* Header row */}
      <div className="px-5 py-4 border-b border-stone-100 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          {r.picture
            ? <img src={r.picture} alt="" className="w-10 h-10 rounded-full object-cover border border-stone-200" />
            : <UserCircle size={40} weight="duotone" className="text-stone-400" />}
          <div className="min-w-0">
            <div className="text-stone-900 font-medium truncate">
              {r.name} <span className="text-stone-400">·</span> <span className="text-stone-600 text-sm">{r.email}</span>
            </div>
            <div className="text-[11px] text-stone-500 truncate">
              user_id: <code className="text-[11px]">{r.user_id}</code>
              <CopyButton value={r.user_id} />
              <span className="mx-2">·</span>
              Joined {fmtDateTime(r.created_at)}
            </div>
          </div>
        </div>
        <div className="flex gap-4 text-right">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Wallet now</div>
            <div className="font-serif-display text-2xl text-emerald-700" data-testid="wallet-balance">
              ₹{r.wallet_balance.toFixed(2)}
            </div>
          </div>
          {r.vip_credit_remaining > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500">VIP credit</div>
              <div className="font-serif-display text-lg text-amber-700">₹{r.vip_credit_remaining.toFixed(2)}</div>
            </div>
          )}
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-stone-100 border-b border-stone-100">
        <SummaryBox label="Shown here" value={`₹${(s.shown_total_inr || 0).toFixed(2)}`} sub={`last ${s.deductions_shown || 0}`} />
        <SummaryBox label="Lifetime chats" value={`₹${(s.lifetime_chat_total_inr || 0).toFixed(2)}`} sub="all-time" />
        <SummaryBox label="Lifetime unlocks" value={`₹${(s.lifetime_unlock_total_inr || 0).toFixed(2)}`} sub="all-time" />
        <SummaryBox
          label="Total deducted"
          value={`₹${(s.lifetime_deducted_inr || 0).toFixed(2)}`}
          sub="lifetime"
          highlight
          testId="lifetime-total"
        />
      </div>

      {/* Deduction ledger */}
      {(!r.deductions || r.deductions.length === 0) ? (
        <div className="p-8 text-center text-stone-500 text-sm">
          No wallet deductions on record for this customer.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="deductions-table">
            <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">When</th>
                <th className="text-left px-4 py-2 font-medium">Source</th>
                <th className="text-left px-4 py-2 font-medium">Description</th>
                <th className="text-right px-4 py-2 font-medium">Amount</th>
                <th className="text-left px-4 py-2 font-medium">Ref</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {r.deductions.map((d, i) => (
                <tr key={`${d.source}-${d.ref_id}-${i}`} data-testid={`deduction-row-${i}`}>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-stone-900">{fmtDateTime(d.timestamp)}</div>
                    <div className="text-[11px] text-stone-500">{fmtRelative(d.timestamp)}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <SourcePill source={d.source} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-stone-900">{d.description}</div>
                    {d.source === "chat" && d.is_ai_astrologer && (
                      <div className="text-[11px] text-violet-700 mt-0.5">AI astrologer</div>
                    )}
                    {d.source === "chat" && d.chat_status && d.chat_status !== "ended" && (
                      <div className="text-[11px] text-amber-700 mt-0.5">chat status: {d.chat_status}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="text-rose-700 font-semibold">− ₹{d.amount_inr.toFixed(2)}</div>
                  </td>
                  <td className="px-4 py-3">
                    {d.ref_id && (
                      <code className="text-[11px] text-stone-500 break-all">
                        {d.ref_id}<CopyButton value={d.ref_id} />
                      </code>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryBox({ label, value, sub, highlight, testId }) {
  return (
    <div
      className={`p-4 ${highlight ? "bg-amber-50" : "bg-white"}`}
      data-testid={testId || `summary-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`font-serif-display text-lg mt-0.5 ${highlight ? "text-amber-800" : "text-stone-900"}`}>{value}</div>
      {sub && <div className="text-[11px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}
